import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { i18n, log } from '~/utils';

import { queryKeysCompany } from './queryKeys';
import {
  zodCompanyDataSchema,
  zodCompanyResponseSchema,
} from './useMutationCreateCompanyAdmin';

const zodCompanyDataUpdateSchema = zodCompanyDataSchema.omit({
  companyAccountId: true,
});

const zodUpdateCompanyEndpointSchema = z.tuple([
  z.literal('vadmin'),
  z.literal('company'),
  z.uuid(),
]);

type CompanyData = z.infer<typeof zodCompanyDataSchema>;

const zodUpdateCompanyResponseSchema = zodCompanyResponseSchema;

type UpdateCompanyResponse = z.infer<typeof zodUpdateCompanyResponseSchema>;

type UpdateCompanyVariables = {
  companyId: UUID;
  companyData: CompanyData;
};

// Fields where an empty string means "clear the value": send an explicit
// null instead of dropping the key so the API overwrites the stored value.
const CLEARABLE_FIELDS = [
  'gln',
  'hra',
  'hraRegisterCourt',
  'hrb',
  'interchangeGln',
  'registerCourt',
  'syncKey',
  'vatId',
] as const;

export const toUpdatePayload = (companyData: CompanyData) => {
  // company account id can not be changed once set
  const { companyAccountId: _companyAccountId, ...payload } =
    withoutObjectKeysWhereValueIs(companyData, [null, undefined, '']);

  for (const field of CLEARABLE_FIELDS) {
    if (companyData[field] !== undefined) {
      payload[field] = companyData[field] || null;
    }
  }

  return payload;
};

/**
 * Updates a company as Admin.
 *
 * @param companyData - An object containing the company details to be created.
 * @returns - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/update_company_by_id_vadmin_company__company_id__patch
 */
// fallow-ignore-next-line unused-export
export const updateCompanyAdmin = async (
  companyId: UUID,
  companyData: CompanyData,
) => {
  try {
    return await vestigasApi
      .patch(ENDPOINT.COMPANY.UPDATE_ADMIN(companyId), {
        json: toUpdatePayload(companyData),
        validate: {
          body: zodCompanyDataUpdateSchema,
          pathSegments: zodUpdateCompanyEndpointSchema,
          response: zodUpdateCompanyResponseSchema,
        },
      })
      .json<UpdateCompanyResponse>();
  } catch (error) {
    log.error({
      message: 'Error updating company',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 409: {
          errorMessage = i18n.t(($) => $.company.updateCompany.error409, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.company.updateCompany.error.generic, {
            ns: 'data',
          });
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * Custom hook for updating a company as Admin using mutations.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/update_company_by_id_vadmin_company__company_id__patch
 */
export const useMutationUpdateCompanyAdmin = (
  options?: Omit<
    UseMutationOptions<UpdateCompanyResponse, Error, UpdateCompanyVariables>,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<UpdateCompanyResponse, Error, UpdateCompanyVariables>({
    mutationFn: async ({ companyId, companyData }) =>
      updateCompanyAdmin(companyId, companyData),
    ...options,
    onSettled(data, error, variables, onMutateResult, context) {
      queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getAdmin(variables.companyId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getAllAdmin({}),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getByIdsAdminBase(),
      });

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
  });
};
